import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const emptyForm = {
  name: "",
  designation: "",
  subject: "",
  qualification: "",
  bio: "",
  display_order: 0,
};

function SchoolTeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await axiosInstance.get("/api/team/school");
      setMembers(res.data);
    } catch (err) {
      toast.error("Failed to load school team");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPhotoFile(null);
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setEditingId(member.id);
    setForm({
      name: member.name,
      designation: member.designation || "",
      subject: member.subject || "",
      qualification: member.qualification || "",
      bio: member.bio || "",
      display_order: member.display_order,
    });
    setPhotoFile(null);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    if (photoFile) formData.append("photo", photoFile);

    try {
      if (editingId) {
        await axiosInstance.put(`/api/team/school/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Staff member updated");
      } else {
        await axiosInstance.post("/api/team/school", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Staff member added");
      }
      setShowModal(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from school team?`)) return;
    try {
      await axiosInstance.delete(`/api/team/school/${id}`);
      toast.success("Removed");
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">School Team</h4>
        <button className="btn btn-primary" onClick={openAddModal}>
          <i className="bi bi-plus-lg me-1"></i> Add Staff
        </button>
      </div>

      <div className="row g-3">
        {members.length === 0 ? (
          <p className="text-muted">No staff added yet.</p>
        ) : (
          members.map((m) => (
            <div className="col-12 col-sm-6 col-lg-4" key={m.id}>
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <img
                    src={
                      m.photo_path
                        ? `${process.env.REACT_APP_API_URL}/${m.photo_path}`
                        : "https://via.placeholder.com/90?text=Photo"
                    }
                    alt={m.name}
                    className="rounded-circle mb-3"
                    style={{
                      width: "90px",
                      height: "90px",
                      objectFit: "cover",
                    }}
                  />
                  <h6 className="mb-0">{m.name}</h6>
                  <p className="text-muted small mb-1">
                    {m.designation || "—"}
                  </p>
                  {m.subject && (
                    <span className="badge bg-light text-dark border">
                      {m.subject}
                    </span>
                  )}
                  <div className="d-flex justify-content-center gap-2 mt-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => openEditModal(m)}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(m.id, m.name)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingId ? "Edit Staff" : "Add Staff"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>

                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label className="form-label">Designation</label>
                        <input
                          type="text"
                          className="form-control"
                          name="designation"
                          value={form.designation}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label className="form-label">Subject</label>
                        <input
                          type="text"
                          className="form-control"
                          name="subject"
                          value={form.subject}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Qualification</label>
                      <input
                        type="text"
                        className="form-control"
                        name="qualification"
                        value={form.qualification}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Bio</label>
                      <textarea
                        className="form-control"
                        name="bio"
                        rows="3"
                        value={form.bio}
                        onChange={handleFormChange}
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={form.display_order}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="mb-1">
                      <label className="form-label">Photo</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={(e) => setPhotoFile(e.target.files[0])}
                      />
                      {editingId && (
                        <small className="text-muted">
                          Leave empty to keep the current photo
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : editingId ? "Update" : "Add"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default SchoolTeamPage;
